import React from 'react';
import { ConfigProvider } from 'antd';
import enUS from 'antd/es/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/en';

/**
 * Umi runtime root container.
 *
 * antd v3 synced locale + design tokens automatically via umi-plugin-react.
 * antd v5 uses CSS-in-JS with different defaults; we restore the v3 values
 * here so every page looks identical to the master-branch (antd v3) production.
 *
 * Key v3→v5 default differences we correct:
 *   borderRadius:     2px  → 6px   (makes all inputs/buttons/tags/cards more rounded)
 *   colorPrimary:     #1890ff → #1677ff  (different blue)
 *   colorText:        rgba(0,0,0,.85) → rgba(0,0,0,.88)  (slightly darker)
 *   colorTextSecondary: rgba(0,0,0,.45) → rgba(0,0,0,.65)
 *   colorBgLayout:    #f0f2f5 → #f5f5f5  (background behind pages — antd v3 had blue-grey)
 *   colorLink:        same primary → different blue
 */
dayjs.locale('en');

export function rootContainer(container: React.ReactNode) {
  return (
    <ConfigProvider
      locale={enUS}
      theme={{
        token: {
          // Primary / link colors — match antd v3 #1890ff brand blue
          colorPrimary: '#1890ff',
          colorLink: '#1890ff',
          colorLinkHover: '#40a9ff',
          colorLinkActive: '#096dd9',

          // Border radius — antd v3 used 2px; v5 defaults to 6px
          borderRadius: 2,
          borderRadiusSM: 2,
          borderRadiusLG: 4,
          borderRadiusXS: 2,

          // Text colors — antd v3 values
          colorText: 'rgba(0,0,0,0.85)',
          colorTextSecondary: 'rgba(0,0,0,0.45)',
          colorTextDisabled: 'rgba(0,0,0,0.25)',

          // Page/layout background — antd v3 blue-grey (#f0f2f5)
          colorBgLayout: '#f0f2f5',

          // Border
          colorBorder: '#d9d9d9',
          colorBorderSecondary: '#f0f0f0',

          // Font
          fontSize: 14,
          lineHeight: 1.5715,
        },
      }}
    >
      {container}
    </ConfigProvider>
  );
}
